import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import { LOG_LEVELS, precisionCallout, formatLogLineTime } from '../utils/bleLogOverlay';
import './BleLogOverlay.css';

/**
 * BLE Log Overlay pieces rendered by SessionDetailView when a GogglesLogCaptured row's
 * log is laid into the timeline. See src/utils/bleLogOverlay.js for the placement rules.
 */

// Height of the app's own sticky header, so the pinned bar sits just below it.
const pinOffset = () => {
  const header = document.querySelector('.app-header');
  return header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
};

/**
 * Keeps the bar pinned to the top of the viewport while the timeline scrolls under it.
 * position: sticky cannot do this here: the document scrolls, but .session-detail-timeline
 * and main.user-detail-content set overflow: auto, which ties a sticky element to boxes that
 * never scroll. So the bar stays in flow in a slot, and while the slot is above the pin line
 * and the timeline is still on screen, it renders position: fixed over the slot's column.
 */
const usePinnedBar = () => {
  const slotRef = useRef(null);
  const barRef = useRef(null);
  const [pin, setPin] = useState(null); // { top, left, width, height } | null

  useEffect(() => {
    let frame = null;
    const update = () => {
      frame = null;
      const slot = slotRef.current;
      const bar = barRef.current;
      if (!slot || !bar) return;
      const timeline = slot.closest('.session-detail-timeline') || slot.parentElement;
      const top = pinOffset();
      const slotRect = slot.getBoundingClientRect();
      const height = bar.offsetHeight;
      const shouldPin = slotRect.top < top && timeline.getBoundingClientRect().bottom > top + height;
      setPin((prev) => {
        if (!shouldPin) return null;
        const next = { top, left: slotRect.left, width: slotRect.width, height: prev?.height ?? height };
        return prev && prev.top === next.top && prev.left === next.left && prev.width === next.width ? prev : next;
      });
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };
    update();
    // Capture phase: catches scrolls of the document and of any scrolling ancestor.
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return { slotRef, barRef, pin };
};

/**
 * Control bar at the top of the timeline while an overlay is open; pins below the app
 * header while scrolling (usePinnedBar).
 * Props: filename, lineCount, levelCounts ({ E: n, ... }), levels (Set of visible levels),
 * onToggleLevel(level), unknownCount, onOpenUnknown(), onClose()
 */
export const BleLogOverlayBar = ({
  filename,
  lineCount,
  levelCounts,
  levels,
  onToggleLevel,
  unknownCount,
  onOpenUnknown,
  onClose,
}) => {
  const { slotRef, barRef, pin } = usePinnedBar();
  return (
    <div ref={slotRef} className="ble-log-bar-slot" style={pin ? { height: pin.height } : {}}>
      <div
        ref={barRef}
        className={`ble-log-bar ${pin ? 'is-pinned' : ''}`}
        style={pin ? { position: 'fixed', top: pin.top, left: pin.left, width: pin.width } : {}}
      >
        <span className="ble-log-bar-label">BLE LOG</span>
        <span className="ble-log-bar-file">{filename}</span>
        <span className="ble-log-bar-count">{lineCount} {lineCount === 1 ? 'line' : 'lines'}</span>
        <div className="ble-log-bar-levels">
          {LOG_LEVELS.filter((level) => levelCounts[level]).map((level) => (
            <button
              key={level}
              type="button"
              className={`ble-log-level-chip level-${level}`}
              aria-pressed={levels.has(level)}
              title={levels.has(level) ? `Hide ${level} lines` : `Show ${level} lines`}
              onClick={() => onToggleLevel(level)}
            >
              {level}
              <span className="ble-log-level-count">{levelCounts[level]}</span>
            </button>
          ))}
        </div>
        {unknownCount > 0 && (
          <button type="button" className="ble-log-unknown-button" onClick={onOpenUnknown}>
            {unknownCount} {unknownCount === 1 ? 'line' : 'lines'} with unknown time
          </button>
        )}
        <span className="ble-log-bar-spacer" />
        <button type="button" className="ble-log-bar-close" onClick={onClose}>
          Hide
        </button>
      </div>
    </div>
  );
};

/**
 * One bordered block of consecutive log lines.
 * Props: records, timeZone, sessionsById (Map sessionId -> parsed session),
 * variant: 'inline' | 'before' | 'after'
 */
export const BleLogRun = ({ records, timeZone, sessionsById, variant = 'inline' }) => {
  const title = variant === 'before' ? 'Before first event' : variant === 'after' ? 'After last event' : null;
  return (
    <div className={`ble-log-run variant-${variant}`}>
      <div className="ble-log-run-marker" />
      <div className="ble-log-run-box">
        {title && (
          <div className="ble-log-run-title">
            {title} · {records.length} {records.length === 1 ? 'line' : 'lines'}
          </div>
        )}
        {records.map((record) => (
          <div key={record.sourceLine} className={`ble-log-line level-${record.level}`}>
            <span className="ble-log-line-time">
              {formatLogLineTime(record.timestampMs, timeZone)}
              <span
                className="ble-log-line-asterisk"
                tabIndex={0}
                data-tip={precisionCallout(sessionsById.get(record.sessionId))}
                aria-label={precisionCallout(sessionsById.get(record.sessionId))}
              >
                *
              </span>
            </span>
            <span className="ble-log-line-level">{record.level}</span>
            <span className="ble-log-line-tag" title={record.tag || ''}>{record.tag}</span>
            <span className="ble-log-line-message">{record.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const UnknownGroup = ({ title, records }) => (
  <div className="ble-log-unknown-group">
    <h4 className="ble-log-unknown-group-title">{title}</h4>
    {records.map((record) => (
      <div key={record.sourceLine} className="ble-log-unknown-line">
        <span className="ble-log-unknown-source">line {record.sourceLine}</span>
        <span className="ble-log-unknown-raw">{record.raw}</span>
      </div>
    ))}
  </div>
);

/**
 * Lists the lines the overlay cannot place on the timeline (collectUnknownTimeRecords).
 * Props: isOpen, onClose, groups
 */
export const BleLogUnknownPanel = ({ isOpen, onClose, groups }) => (
  <Modal isOpen={isOpen} onClose={onClose} title="Lines with unknown time" className="ble-log-unknown-modal">
    {groups && (
      <div className="ble-log-unknown-groups">
        {groups.untimed.map((g) => (
          <UnknownGroup key={`untimed-${g.sessionId}`} title={`Session ${g.sessionId} · untimed or unparseable`} records={g.records} />
        ))}
        {groups.undatedSessions.map((g) => (
          <UnknownGroup key={`undated-${g.sessionId}`} title={`Session ${g.sessionId} · no clock anchor (uptime order)`} records={g.records} />
        ))}
        {groups.beforeFirstMarker.length > 0 && (
          <UnknownGroup title="Before the first session marker" records={groups.beforeFirstMarker} />
        )}
      </div>
    )}
  </Modal>
);
